import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, FormBuilder, ActionBar } from "@redriver/cinnamon";
import {
  listGroupPermissions,
  toggleExpandedNode,
  setInScopeForPermissionNode,
  updateSitePermissions,
} from "./actions";
import {
  Checkbox as SCheckbox,
  Table as STable,
  Loader,
  Button,
} from "semantic-ui-react";
import { ArrowIcon } from "components/icons";
import {
  getGroupPermissionsDetails,
  getGroupPermissionExpanded,
  getGroupPermissionLoading,
  getGroupPermissionInScope,
  getGroupPermissionSaving,
  getGroupPermissionHasPartialChildScope,
} from "./selectors";
import { MaplesLabel } from "components/containers";
import { DirectoryIcon, FileIcon, SitesIcon } from "components/icons";
import { SitePermissionItemType } from "constants/enums";
import classNames from "classnames";

const maxIndentDepth = 10;

const GroupPermissionsList = ({ groupId, permission }) => {
  const dispatch = useDispatch();
  const saving = useSelector((s) => getGroupPermissionSaving(s));
  const permissionDetails = useSelector((s) => getGroupPermissionsDetails(s));
  const { permissionData, loading } = permissionDetails;

  const [filterData, setFilterData] = useState({ search: "" });

  const filteredPermissionData = useMemo(() => {
    return permissionData.filter((x) =>
      filterData.search
        ? x.name.toLowerCase().includes(filterData.search.toLowerCase().trim())
        : true
    );
  }, [filterData.search, permissionData]);

  useEffect(() => {
    dispatch(listGroupPermissions(groupId));
  }, []);

  return (
    <div className="site-permissions">
      <FormBuilder
        value={filterData}
        onChange={setFilterData}
        renderForm={(formProps) => {
          return (
            <Form {...formProps}>
              <ActionBar>
                <ActionBar.Item>
                  <Form.Input field="search" placeholder="Search site" />
                </ActionBar.Item>
                <ActionBar.Item>
                  <span style={{ lineHeight: "2.5rem" }}>
                    This group grants{" "}
                    <MaplesLabel type="default">{permission}</MaplesLabel>{" "}
                    access to the items selected below.
                  </span>
                </ActionBar.Item>
                <ActionBar.Item fluid align="right">
                  <Button
                    onClick={() => dispatch(updateSitePermissions())}
                    loading={saving}
                  >
                    Save
                  </Button>
                </ActionBar.Item>
              </ActionBar>
            </Form>
          );
        }}
      />

      <STable>
        <STable.Header>
          <STable.Row>
            <STable.HeaderCell>Name</STable.HeaderCell>
            <STable.HeaderCell className="permission-column">
              In scope
            </STable.HeaderCell>
          </STable.Row>
        </STable.Header>
        <STable.Body>
          {loading && (
            <STable.Row>
              <STable.Cell colSpan="2">
                <div className="loading-row">
                  <Loader active />
                </div>
              </STable.Cell>
            </STable.Row>
          )}
          {!loading &&
            filteredPermissionData.map((x) => (
              <PermissionRow key={x.id} item={x} />
            ))}
          {!loading && filteredPermissionData.length == 0 && (
            <STable.Row>
              <STable.Cell colSpan={2} style={{ textAlign: "center" }}>
                No sites found
              </STable.Cell>
            </STable.Row>
          )}
        </STable.Body>
      </STable>
    </div>
  );
};

const PermissionRow = ({ item, depth = 0, getParentIds = () => [] }) => {
  const dispatch = useDispatch();
  const inScope = useSelector((s) => getGroupPermissionInScope(s, item.id));
  const isExpanded = useSelector((s) => getGroupPermissionExpanded(s, item.id));
  const isLoadingChildren = useSelector((s) =>
    getGroupPermissionLoading(s, item.id)
  );
  // parent ids are in order they are accessed from the root
  const parentIds = useMemo(() => {
    return getParentIds ? getParentIds() : [];
  }, [getParentIds]);
  const getNextParentIds = useCallback(() => [...parentIds, item.id], [
    parentIds,
  ]);

  const hasPartialChildScope = useSelector((s) =>
    getGroupPermissionHasPartialChildScope(s, item.id)
  );

  const saving = useSelector((s) => getGroupPermissionSaving(s));

  return (
    <React.Fragment>
      <STable.Row>
        <STable.Cell>
          <div
            className="item-title"
            style={{
              marginLeft: `calc(1rem * ${
                depth > maxIndentDepth ? maxIndentDepth : depth
              })`,
            }}
            data-node-id={item.id}
          >
            <span
              className={classNames(
                "item-name-group",
                item.hasChildItems ? "pointer" : null
              )}
              onClick={() =>
                item.hasChildItems
                  ? dispatch(toggleExpandedNode(item, parentIds))
                  : null
              }
            >
              <span
                className={classNames(
                  "arrow-wrapper",
                  item.hasChildItems ? null : "hidden"
                )}
              >
                <ArrowIcon direction={isExpanded ? "down" : "right"} />
              </span>
              <span className="icon-wrapper">
                {item.type == SitePermissionItemType.Site ? (
                  <SitesIcon className="site-icon" />
                ) : item.type == SitePermissionItemType.Directory ? (
                  <DirectoryIcon inline />
                ) : (
                  <FileIcon inline file={item} />
                )}
              </span>
              <span className="name-text">{item.name}</span>
            </span>
            {isLoadingChildren && <Loader inline active size="tiny" />}
          </div>
        </STable.Cell>
        <STable.Cell>
          <SCheckbox
            className={classNames({
              minor: hasPartialChildScope && !inScope,
              partial: hasPartialChildScope,
            })}
            checked={inScope}
            disabled={saving}
            onChange={(e, { checked }) => {
              const fillFromPartial =
                !checked && inScope && hasPartialChildScope;

              dispatch(
                setInScopeForPermissionNode(
                  item,
                  parentIds,
                  fillFromPartial ? true : checked
                )
              );
            }}
          />
        </STable.Cell>
      </STable.Row>
      {isExpanded &&
        Array.isArray(item.items) &&
        item.items.map((x) => (
          <PermissionRow
            key={x.id}
            item={x}
            depth={depth + 1}
            getParentIds={getNextParentIds}
          />
        ))}
    </React.Fragment>
  );
};

export default GroupPermissionsList;
